/**
 * #1001 — lift [Disc N] / CD N / Disc N leaves to the album parent so
 * root-level strays and disc-subfolder tracks group as one local album.
 */

export type DiscGroupAlbum = Record<string, unknown>;

export type DiscGroupResult<T extends DiscGroupAlbum> = {
  albums: T[];
  collapsed: number;
};

export function isDiscMediaFolder(name: string): boolean {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return false;
  const inner = stripWrappingBrackets(trimmed);
  const lower = inner.toLowerCase();
  return (
    discStemHasNumber(lower, 'disc')
    || discStemHasNumber(lower, 'disk')
    || discStemHasNumber(lower, 'cd')
  );
}

function stripWrappingBrackets(name: string): string {
  if (
    (name.startsWith('[') && name.endsWith(']'))
    || (name.startsWith('(') && name.endsWith(')'))
  ) {
    return name.slice(1, -1).trim();
  }
  return name;
}

function discStemHasNumber(lower: string, stem: string): boolean {
  if (!lower.startsWith(stem)) return false;
  const rest = lower.slice(stem.length).replace(/^[\s\-_.]+/, '');
  return rest.length > 0 && rest.charCodeAt(0) >= 48 && rest.charCodeAt(0) <= 57;
}

function lastPathComponent(path: string): string {
  const parts = String(path ?? '').split(/[/\\]/).filter(Boolean);
  return parts[parts.length - 1] || '';
}

function parentPath(path: string): string {
  const normalized = String(path ?? '').replace(/[/\\]+$/, '');
  const cut = Math.max(normalized.lastIndexOf('/'), normalized.lastIndexOf('\\'));
  if (cut <= 0) return normalized;
  return normalized.slice(0, cut);
}

function looksLikeAudioFile(name: string): boolean {
  return /\.(flac|aiff|aif|aifc|wav|wave|dsf|dff|mp3|m4a|alac|ogg|wma|ape|wv|opus)$/i.test(name);
}

/** Album directory for a sample file or folder path, lifting disc/CD leaves. */
export function liftDiscFolderPath(path: string): string {
  const raw = String(path ?? '').trim();
  if (!raw) return '';
  let folder = raw.replace(/[/\\]+$/, '');
  if (looksLikeAudioFile(lastPathComponent(folder))) {
    folder = parentPath(folder);
  }
  if (isDiscMediaFolder(lastPathComponent(folder))) {
    const parent = parentPath(folder);
    if (parent && parent !== folder) return parent;
  }
  return folder;
}

function normalizeTitle(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeArtist(value: unknown): string {
  let s = normalizeTitle(value);
  if (s.startsWith('the ')) s = s.slice(4);
  return s;
}

function albumFolderFromRow(album: DiscGroupAlbum): string {
  const sample = String(album.sample_file_path ?? album.sampleFilePath ?? '').trim();
  if (sample) return liftDiscFolderPath(sample);
  const folder = String(
    album.library_folder_path ?? album.libraryFolderPath ?? album.folder_path ?? '',
  ).trim();
  return liftDiscFolderPath(folder);
}

/** Album tag + lifted parent folder. Empty when either side is missing. */
export function discGroupKey(album: DiscGroupAlbum | null | undefined): string {
  if (!album) return '';
  const title = normalizeTitle(album.title ?? album.album);
  const folder = albumFolderFromRow(album).toLowerCase();
  if (!title || !folder) return '';
  const artist = normalizeArtist(
    album.album_artist ?? album.albumArtist ?? album.artist_name ?? album.artist,
  );
  return `${title}\u001f${artist}\u001f${folder}`;
}

function rowScore(album: DiscGroupAlbum): number {
  return Number(album.track_count) || 0;
}

function mergePair<T extends DiscGroupAlbum>(a: T, b: T): T {
  const richer = rowScore(a) >= rowScore(b) ? a : b;
  const other = richer === a ? b : a;
  const merged: T = { ...other, ...richer };
  merged.track_count = Math.max(Number(a.track_count) || 0, Number(b.track_count) || 0);
  return merged;
}

function pathHasDiscMediaLeaf(path: string): boolean {
  const raw = String(path ?? '').trim();
  if (!raw) return false;
  let folder = raw.replace(/[/\\]+$/, '');
  if (looksLikeAudioFile(lastPathComponent(folder))) {
    folder = parentPath(folder);
  }
  return isDiscMediaFolder(lastPathComponent(folder));
}

function albumsHaveDiscFolderLayout(albums: DiscGroupAlbum[]): boolean {
  for (const album of albums) {
    const sample = String(album.sample_file_path ?? album.sampleFilePath ?? '').trim();
    if (sample && pathHasDiscMediaLeaf(sample)) return true;
    const folder = String(
      album.library_folder_path ?? album.libraryFolderPath ?? album.folder_path ?? '',
    ).trim();
    if (folder && pathHasDiscMediaLeaf(folder)) return true;
  }
  return false;
}

/** Collapse root-stray + Disc/CD subfolder rows that share album tag + parent. */
export function groupDiscFolderSiblings<T extends DiscGroupAlbum>(
  albums: T[] | null | undefined,
): DiscGroupResult<T> {
  if (!albums?.length) return { albums: albums ?? [], collapsed: 0 };
  if (!albumsHaveDiscFolderLayout(albums)) return { albums, collapsed: 0 };
  const order: string[] = [];
  const byKey = new Map<string, T>();
  let collapsed = 0;

  for (let i = 0; i < albums.length; i += 1) {
    const row = albums[i];
    if (!row) continue;
    const key = discGroupKey(row) || `__row_${i}`;
    const prev = byKey.get(key);
    if (prev) {
      collapsed += 1;
      byKey.set(key, mergePair(prev, row));
      continue;
    }
    order.push(key);
    byKey.set(key, row);
  }

  return {
    albums: order.map((key) => byKey.get(key)!),
    collapsed,
  };
}
